import { defineStore } from 'pinia';

export const useOptionsStore = defineStore('options', {
    state: () => ({
        roles: [],
        statuses: [],
        transactionStatuses: [],
        provinces: [],
        twoFactorMethods: [],
        securityQuestionTemplates: [],
        securityCheckpoint: false,
        offerOptionLists: {
            yesNo: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
            ],
            overallConditions: [
                { label: 'Major Repairs Required', value: '1' },
                { label: 'Repairs Needed', value: '2' },
                { label: 'Well Maintained (Default)', value: '3' },
                { label: 'Like New', value: '4' },
                { label: 'New', value: '5' }
            ],
            age: [
                { label: 'New', value: 'New' },
                { label: '0-5 Years', value: '0-5' },
                { label: '6-10 Years', value: '6-10' },
                { label: '11-15 Years', value: '11-15' },
                { label: '16-30 Years', value: '16-30' },
                { label: '31-50 Years', value: '31-50' },
                { label: '51-99 Years', value: '51-99' },
                { label: '100+ Years', value: '100+' },
                { label: 'Unknown', value: 'Unknown' },
            ],
            propertyTypes: [
                { label: 'Detached', value: 'Detached', allowed: ['freehold'] },
                { label: 'Semi-Detached', value: 'Semi-Detached', allowed: ['freehold'] },
                { label: 'Att/Row/Twnhouse', value: 'Att/Row/Twnhouse', allowed: ['freehold'] },
                { label: 'Duplex', value: 'Duplex', allowed: ['freehold'] },
                { label: 'Triplex', value: 'Triplex', allowed: ['freehold'] },
                { label: 'Fourplex', value: 'Fourplex', allowed: ['freehold'] },
                { label: 'Multiplex', value: 'Multiplex', allowed: ['freehold'] },
                { label: 'Comm Element Condo', value: 'Comm Element Condo', allowed: ['condo'] },
                { label: 'Condo Apt', value: 'Condo Apt', allowed: ['condo'] },
                { label: 'Condo Townhouse', value: 'Condo Townhouse', allowed: ['condo'] },
            ],
            styles: [
                // Freehold & Apartment styles
                { label: '1 1/2 Storey', value: '1 1/2 Storey', allowed: ['freehold'] },
                { label: '2-Storey', value: '2-Storey', allowed: ['freehold'] },
                { label: '2 1/2 Storey', value: '2 1/2 Storey', allowed: ['freehold'] },
                { label: '3-Storey', value: '3-Storey', allowed: ['freehold'] },
                { label: 'Backsplit 3', value: 'Backsplit 3', allowed: ['freehold'] },
                { label: 'Backsplit 4', value: 'Backsplit 4', allowed: ['freehold'] },
                { label: 'Backsplit 5', value: 'Backsplit 5', allowed: ['freehold'] },
                { label: 'Bungaloft', value: 'Bungaloft', allowed: ['freehold'] },
                { label: 'Bungalow', value: 'Bungalow', allowed: ['freehold'] },
                { label: 'Bungalow-Raised', value: 'Bungalow-Raised', allowed: ['freehold'] },
                { label: 'Sidesplit 3', value: 'Sidesplit 3', allowed: ['freehold'] },
                { label: 'Sidesplit 4', value: 'Sidesplit 4', allowed: ['freehold'] },
                { label: 'Sidesplit 5', value: 'Sidesplit 5', allowed: ['freehold'] },
                { label: 'Other', value: 'Other', allowed: ['freehold'] },
                // Condo styles
                { label: 'Apartment', value: 'Apartment', allowed: ['condo'] },
                { label: 'Bachelor/Studio', value: 'Bachelor/Studio', allowed: ['condo'] },
                { label: 'Loft', value: 'Loft', allowed: ['condo'] },
                { label: 'Multi-Level', value: 'Multi-Level', allowed: ['condo'] },
                { label: 'Stacked Townhse', value: 'Stacked Townhse', allowed: ['condo'] },
                { label: 'Other', value: 'Other', allowed: ['condo'] },
            ],
            bedrooms: ['1', '2', '3', '4', '5+'],
            bathrooms: ['1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5+'],
            kitchens: ['1', '2+'],
            basementDevelopment: [
                { label: 'Apartment', value: 'Apartment' },
                { label: 'Crawl Space', value: 'Crawl Space' },
                { label: 'Fin W/O', value: 'Fin W/O' },
                { label: 'Finished', value: 'Finished' },
                { label: 'Full', value: 'Full' },
                { label: 'Half', value: 'Half' },
                { label: 'Other', value: 'Other' },
                { label: 'Part Bsmt', value: 'Part Bsmt' },
                { label: 'Part Fin', value: 'Part Fin' },
                { label: 'Sep Entrance', value: 'Sep Entrance' },
                { label: 'Unfinished', value: 'Unfinished' },
                { label: 'W/O', value: 'W/O' },
                { label: 'Walk Up', value: 'Walk Up' },
            ],
            heatingTypes: [
                'Forced Air Furnace',
                'Electric Baseboard',
                'Hydronic Radiators (Hot Water/Baseboard)',
                'Radiant In-Floor (Hydronic)',
                'Radiant Panels (Electric)',
                'Heat Pump — Air-Source (Ducted)',
                'Heat Pump — Ductless Mini-Split',
                'Heat Pump — Ground-Source/Geothermal',
                'Steam Radiators',
                'Wood Stove',
                'Pellet Stove',
                'Space Heater (Gas/Electric)',
                'Fireplace (Supplemental)',
                'District/Shared Heating',
                'Other',
                'None'
            ],
            garageParking: [
                { label: 'Attached', value: 'attached' },
                { label: 'Detached', value: 'detached' },
                { label: 'Built-In', value: 'built_in' },
                { label: 'Carport', value: 'carport' },
                { label: 'Underground', value: 'underground' },
                { label: 'Surface', value: 'surface' },
                { label: 'None', value: 'none' },
                { label: 'Other', value: 'other' },
            ],
            floorCoverings: ['Hardwood', 'Carpet', 'Tile', 'Laminate', 'Vinyl Plank', 'Concrete', 'Other'],
            timelines: [
                { label: 'ASAP', value: 'asap' },
                { label: '1-2 Weeks', value: '1_2_weeks' },
                { label: '1 Month', value: '1_month' },
                { label: 'Flexible', value: 'flexible' },
            ],
            sellerMotivations: [
                { label: 'Transfer', value: 'transfer' },
                { label: 'Retire', value: 'retire' },
                { label: 'Upgrade', value: 'upgrade' },
                { label: 'Downsize', value: 'downsize' },
                { label: 'Estate', value: 'estate' },
                { label: 'Investment', value: 'investment' },
                { label: 'Other', value: 'other' },
            ],
        }
    }),
    actions: {
        reset() {
            this.$reset();
        },
    },
    getters: {
        roleOptions: (state) => {
            return state.roles;
        },

        provinceOptions: (state) => {
            return state.provinces.map((province) => ({
                label: province.name,
                value: province.id,
            }));
        },

        statusOptions: (state) => {
            return state.statuses.map((status) => ({
                label: status.name,
                value: status.id,
            }));
        },
        // Offer related getters
        yesNoOptions: (state) => state.offerOptionLists.yesNo,
        overallConditionOptions: (state) => state.offerOptionLists.overallConditions,
        propertyTypeOptions: (state) => state.offerOptionLists.propertyTypes,
        styleAllOptions: (state) => state.offerOptionLists.styles,
        bedroomsOptions: (state) => state.offerOptionLists.bedrooms.map(v => ({ label: String(v), value: String(v) })),
        bathroomsOptions: (state) => state.offerOptionLists.bathrooms.map(v => ({ label: v, value: v })),
        kitchensOptions: (state) => state.offerOptionLists.kitchens.map(v => ({ label: v, value: v })),
        basementDevelopmentOptions: (state) => state.offerOptionLists.basementDevelopment,
        heatingTypeOptions: (state) => state.offerOptionLists.heatingTypes.map(v => ({ label: v, value: v })),
        garageParkingOptions: (state) => state.offerOptionLists.garageParking,
        floorCoveringsOptions: (state) => state.offerOptionLists.floorCoverings.map(v => ({ label: v, value: v })),
        timelineOptions: (state) => state.offerOptionLists.timelines,
        sellerMotivationOptions: (state) => state.offerOptionLists.sellerMotivations,
    }
});

// HMR
if (import.meta.hot) {
    import.meta.hot.accept(useOptionsStore.acceptHMRUpdate);
}
